/**
 * Compares props deeply but efficiently without JSON.stringify
 * @param {Object} objA First object to compare
 * @param {Object} objB Second object to compare
 * @param {number} depth Current recursion depth (for safety)
 * @returns {boolean} Whether the objects are equal
 */

function arePropsEqual(objA, objB, depth = 0) {
  // Same reference = equal
  if (objA === objB) return true;
  
  // If one is null/undefined but not the other
  if (!objA || !objB) return false;
  
  // Different types
  const typeA = typeof objA;
  const typeB = typeof objB;
  if (typeA !== typeB) return false;
  
  // Don't compare functions deeply, just check reference (already done above)
  if (typeA === 'function') return true;
  
  // For primitive types, we've already checked === above
  if (typeA !== 'object') return objA === objB;
  
  // For arrays
  if (Array.isArray(objA)) {
    if (!Array.isArray(objB) || objA.length !== objB.length) return false;
    
    // Limit recursion depth for safety
    if (depth > 5) return true;
    
    // Compare array items
    for (let i = 0; i < objA.length; i++) {
      if (!arePropsEqual(objA[i], objB[i], depth + 1)) return false;
    }
    return true;
  }
  
  // For dates
  if (objA instanceof Date) {
    return objB instanceof Date && objA.getTime() === objB.getTime();
  }
  
  // For plain objects
  const keysA = Object.keys(objA);
  const keysB = Object.keys(objB);
  
  if (keysA.length !== keysB.length) return false;
  
  // Limit recursion depth for safety
  if (depth > 5) return true;
  
  // Check all keys from A exist in B and have same values
  return keysA.every(key => 
    Object.prototype.hasOwnProperty.call(objB, key) && 
    arePropsEqual(objA[key], objB[key], depth + 1)
  );
}

export default arePropsEqual;